import {Table, Button, message, Row, Col, Modal, Space, Radio, Tag, Typography, Alert } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { mainColumns } from "./PlaidDetailsTableColumns";
import { useEffect, useState } from "react";
import LoaderComponent from "../../../Loader";
import API from "../../../../services/API";
import CustomText from "../../../CustomText";
import { convertCentsIntoDollar, isSupportLead, isSupportAgent2 } from "../../../../helpers/genericHelper";
import { PLAID_ACCOUNT_STATE } from "../../../../constants/AppConstants";
import PlaidBalance from "./PlaidBalance";
import PlaidEvents from "./PlaidEvents";
import PlaidDetailsRow from "./PlaidDetailsRow";
import usePageState from "../../../../helpers/usePageState";

const { Text, Link } = Typography;

const BULK_UNLINK = "unlink";
const BULK_HIDE = "hide";

const isDeleted = (account) => account.accountState === PLAID_ACCOUNT_STATE.DELETED;

// Same rules as the single-account buttons
const canUnlink = (account) => account.accountState !== PLAID_ACCOUNT_STATE.DELETED
  && account.accountState !== PLAID_ACCOUNT_STATE.REJECTED;
const canHide = (account) => !account.isHidden;

const FILTERS = {
  all: { label: "All", test: () => true },
  connected: { label: "Connected", test: canUnlink },
  deletedShown: { label: "Deleted, still shown to user", test: (account) => isDeleted(account) && !account.isHidden },
  hidden: { label: "Hidden", test: (account) => account.isHidden }
};

const accountLabel = (account) => `${account.bankName || "Unknown bank"} ••${account.last4 || "----"}`;

// What a bulk action will do to one account
const plannedStep = (action, account) => {
  if (action === BULK_UNLINK) return canUnlink(account) ? "Unlink" : null;
  if (!canHide(account)) return null;
  return isDeleted(account) ? "Hide" : "Unlink, then hide";
};

export default function PlaidDetails({ userId, refreshAccount }) {
  const [plaidAccounts, setPlaidAccounts] = useState([]);
  const [plaidLinkAllowed, setPlaidLinkAllowed] = useState(null);
  const [changeActivePlaidModalVisible, setChangeActivePlaidModalVisible] = useState(false);
  const [changeDeletePlaidModalVisible, setChangeDeletePlaidModalVisible] = useState(false);
  const [hidePlaidModalVisible, setHidePlaidModalVisible] = useState(false);
  const [selectedPlaidId, setSelectedPlaidId] = useState(null);
  const [selectedPlaidIsDeleted, setSelectedPlaidIsDeleted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [plaidBalances, setPlaidBalances] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = usePageState("plaid-accounts-rows", ["0"]);

  // Bulk unlink / hide
  const [filter, setFilter] = usePageState("plaid-accounts-filter", "all");
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [bulkAction, setBulkAction] = useState(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  const errorMessage = (content, duration = 10) => {
    message.error(content, duration);
  };

  const showChangeActivePlaidModal = (id) => {
    setSelectedPlaidId(id);
    setChangeActivePlaidModalVisible(true);
  };

  const closeChangeActivePlaidModal = () => {
    setChangeActivePlaidModalVisible(false);
  }

  const showChangeDeletePlaidModal = (id) => {
    setSelectedPlaidId(id);
    setChangeDeletePlaidModalVisible(true);
  };

  const closeChangeDeletePlaidModal = () => {
    setChangeDeletePlaidModalVisible(false);
  }

  const showHidePlaidModal = (id, isDeleted) => {
    setSelectedPlaidId(id);
    setSelectedPlaidIsDeleted(isDeleted);
    setHidePlaidModalVisible(true);
  };

  const closeHidePlaidModal = () => {
    setHidePlaidModalVisible(false);
  }

  const changeActivePlaid = async () => {
    try {
      await API.changeActivePlaidAccount(userId, selectedPlaidId);
      message.success("Successfully set selected plaid account as active.")
      refreshAccount();
    } catch (ex) {
      errorMessage("Unable to set this plaid account to active.");
      errorMessage(ex?.response?.data?.error_message || ex?.response?.data?.errorMessage);
    } finally {
      setChangeActivePlaidModalVisible(false);
    }
  };

  const deletePlaidAccount = async () => {
    try {
      await API.deletePlaidAccount({ userId, plaidAccountId: selectedPlaidId });
      message.success("Successfully deleted plaid account.")
      refreshAccount();
    } catch (ex) {
      errorMessage("Unable to delete plaid account.");
      errorMessage(ex?.response?.data?.error_message || ex?.response?.data?.errorMessage);
    } finally {
      setChangeDeletePlaidModalVisible(false);
    }
  };

  const hidePlaidAccount = async () => {
    try {
      await API.hidePlaidAccount({ userId, plaidAccountId: selectedPlaidId });
      message.success("Successfully hid plaid account from user.");
      refreshAccount();
    } catch (ex) {
      errorMessage("Unable to hide plaid account.");
      errorMessage(ex?.response?.data?.error_message || ex?.response?.data?.errorMessage);
    } finally {
      setHidePlaidModalVisible(false);
    }
  };

  const checkBalance = async (account) => {
    setBalanceLoading(true);
    try {
      let res = await API.getUserBalance({ userId, accountId: account.id });
      message.success(`Tha balance on the account (ending ${account.last4}) is ${convertCentsIntoDollar(res.balanceInCents)}`);
      balanceEntryPanel();
    } catch (ex) {
      errorMessage("Unable to delete plaid account.");
      errorMessage(ex?.response?.data?.error_message || ex?.response?.data?.errorMessage);
    } finally {
      setBalanceLoading(false);
    }
  };

  const plaidAccountDetails = async () => {
    try {
      setLoading(true);
      let res = await API.getPlaidAccountDetails(userId);
      const accountsWithKey = res.plaidConnections.map((account) => ({
          ...account,
          key: account.id,
      }));
      setPlaidAccounts(accountsWithKey);
      setPlaidLinkAllowed(res.plaidLinkAllowed);
    } catch (error) {
      errorMessage("Unable to fetch Plaid Accounts");
      errorMessage(error?.response?.data?.error_message || error?.response?.data?.errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const balanceEntryPanel = async () => {
    try {
      let res = await API.plaidBalances(userId);
      setPlaidBalances(res.plaidBalances);
    } catch (ex) {
      // handle error
    }
  };

  useEffect(() => {
      plaidAccountDetails();
      balanceEntryPanel();
  }, [userId]);

  useEffect(() => {
      if (plaidAccounts.length > 0 && plaidBalances.length > 0) {
          const accountMap = {};
          plaidAccounts.forEach(acc => {
              accountMap[acc.id] = acc.bankName;
          });
          setPlaidBalances(prevBalances =>
              prevBalances.map(balance => ({
                  ...balance,
                  bankName: accountMap[balance.plaidId] || 'Unknown'
              }))
          );
      }
  }, [plaidAccounts, plaidBalances]);

  const selectedAccounts = plaidAccounts.filter((account) => selectedKeys.includes(account.key));
  const selectedConnected = selectedAccounts.filter(canUnlink).length;
  const selectedDeleted = selectedAccounts.filter(isDeleted).length;
  const bulkPlan = bulkAction
    ? selectedAccounts.map((account) => ({ account, step: plannedStep(bulkAction, account) }))
    : [];
  const bulkToRun = bulkPlan.filter((item) => item.step);
  const visibleAccounts = plaidAccounts.filter(FILTERS[filter]?.test || FILTERS.all.test);

  const selectWhere = (test) => {
    setSelectedKeys(plaidAccounts.filter((account) => (canUnlink(account) || canHide(account)) && test(account)).map((a) => a.key));
  };

  // One request per account, one after another, so each result can be reported on its own
  const runBulkAction = async () => {
    setBulkRunning(true);
    const results = [];
    for (const { account, step } of bulkToRun) {
      try {
        if (bulkAction === BULK_UNLINK) {
          await API.deletePlaidAccount({ userId, plaidAccountId: account.id });
        } else {
          // Hiding a connected account also unlinks it, the same as the single Hide button
          await API.hidePlaidAccount({ userId, plaidAccountId: account.id });
        }
        results.push({ account, step, ok: true });
      } catch (ex) {
        results.push({
          account,
          step,
          ok: false,
          error: ex?.response?.data?.error_message || ex?.response?.data?.errorMessage || "Request failed"
        });
      }
    }
    setBulkRunning(false);
    setBulkResults({ action: bulkAction, results });
    setBulkAction(null);
    setSelectedKeys([]);
    // Reload only this list, so the results stay on screen. The rest of the page refreshes on dismiss.
    plaidAccountDetails();
  };

  const retryFailed = () => {
    const failedKeys = bulkResults.results.filter((r) => !r.ok).map((r) => r.account.key);
    setSelectedKeys(failedKeys);
    setBulkAction(bulkResults.action);
    setBulkResults(null);
  };

  const dismissResults = () => {
    setBulkResults(null);
    refreshAccount();
  };

  // The single-account buttons, shown with the account's details
  const rowActions = (record) => (
    <Space wrap style={{ marginBottom: 12 }}>
      {isSupportAgent2() && record.accountState !== PLAID_ACCOUNT_STATE.EXPIRED
          && record.accountState !== PLAID_ACCOUNT_STATE.DELETED
          && record.accountState !== PLAID_ACCOUNT_STATE.REJECTED && (
          <Button type="primary" size="small" onClick={() => showChangeActivePlaidModal(record.id)}>
              {'Override Bank Checks'}
          </Button>
      )}

      {isSupportLead() && record.accountState !== PLAID_ACCOUNT_STATE.EXPIRED
          && record.accountState !== PLAID_ACCOUNT_STATE.DELETED
          && record.accountState !== PLAID_ACCOUNT_STATE.REJECTED && (
          <Button ghost type="primary" size="small" loading={balanceLoading} onClick={() => checkBalance(record)}>
              {'Check Balance'}
          </Button>
      )}

      {isSupportAgent2() && canUnlink(record) && (
          <Button danger size="small" onClick={() => showChangeDeletePlaidModal(record.id)}>
              {'Unlink'}
          </Button>
      )}

      {isSupportAgent2() && (
          <Button
              danger
              size="small"
              onClick={() => showHidePlaidModal(record.id, isDeleted(record))}
              disabled={record.isHidden}
          >
              {record.isHidden ? 'Hidden' : 'Hide in App'}
          </Button>
      )}
    </Space>
  );

  const expandedRowRender = (row) => (
    <>
      {rowActions(row)}
      <PlaidDetailsRow details={row} userId={userId} />
    </>
  );

  const columns = [
    Table.SELECTION_COLUMN,
    Table.EXPAND_COLUMN,
    ...mainColumns.map((column) => column.key === "bankName"
      ? {
          ...column,
          render: (bankName, record) => <Space size={4} wrap>
            {bankName}
            {record.isActive && <Tag color="purple">Primary</Tag>}
          </Space>
        }
      : column),
    {
      title: "Shown to User",
      key: "shownToUser",
      render: (_, record) => {
        if (record.isHidden) return <Tag>Hidden</Tag>;
        if (isDeleted(record)) return <Tag color="warning">Yes, still shown</Tag>;
        return "Yes";
      }
    }
  ];

  const resultCounts = bulkResults && {
    ok: bulkResults.results.filter((r) => r.ok).length,
    failed: bulkResults.results.filter((r) => !r.ok)
  };

  return (
    <>
      {loading && plaidAccounts.length === 0 ? (
        <LoaderComponent />
      ) : (
        <>
          <>
            {plaidLinkAllowed !== 'null' && (
                <Row className="subscription-cancelled-card" gutter={[30, 30]}>
                    <Col xs={24} sm={12} md={8} xl={6} xxl={4}>
                        <h4>No Further Plaid Links Allowed</h4>
                        <CustomText dataSet={{ plaidLinkAllowed }} property1="plaidLinkAllowed" />
                    </Col>
                </Row>
            )}

            {/* Filters and quick select */}
            <div className="plaid-bulk-toolbar">
              <Radio.Group value={filter} onChange={(e) => setFilter(e.target.value)} size="small">
                {Object.entries(FILTERS).map(([key, { label, test }]) => (
                  <Radio.Button key={key} value={key}>{label} ({plaidAccounts.filter(test).length})</Radio.Button>
                ))}
              </Radio.Group>
              {isSupportAgent2() && <Text type="secondary">
                Quick select:{' '}
                <Link onClick={() => selectWhere(FILTERS.deletedShown.test)}>all deleted but shown</Link>
                {' · '}
                <Link onClick={() => selectWhere((account) => !account.isActive)}>all non-primary</Link>
              </Text>}
            </div>

            {/* Results of the last bulk action */}
            {bulkResults &&
              <Alert
                className="plaid-bulk-results"
                type={resultCounts.failed.length ? "warning" : "success"}
                showIcon
                closable
                onClose={dismissResults}
                message={<Space size={8} wrap>
                  {resultCounts.ok > 0 && <Tag color="success">{resultCounts.ok} {bulkResults.action === BULK_UNLINK ? "unlinked" : "hidden"}</Tag>}
                  {resultCounts.failed.length > 0 && <Tag color="error">{resultCounts.failed.length} failed</Tag>}
                  {resultCounts.failed.length > 0 && <Link onClick={retryFailed}>Retry failed</Link>}
                </Space>}
                description={resultCounts.failed.length > 0 && <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {resultCounts.failed.map((r) => <li key={r.account.key}>{accountLabel(r.account)}: {r.error}</li>)}
                </ul>}
              />
            }

            {/* Shown while accounts are selected */}
            {selectedKeys.length > 0 &&
              <div className="plaid-bulk-bar">
                <Text strong style={{ color: '#fff' }}>{selectedKeys.length} selected</Text>
                <Text style={{ color: 'rgba(255,255,255,0.8)' }}>
                  {[selectedConnected && `${selectedConnected} connected`, selectedDeleted && `${selectedDeleted} deleted`].filter(Boolean).join(' · ')}
                </Text>
                <span style={{ flex: 1 }} />
                <Button size="small" ghost disabled={selectedConnected === 0} onClick={() => setBulkAction(BULK_UNLINK)}>
                  Unlink Selected
                </Button>
                <Button size="small" disabled={!selectedAccounts.some(canHide)} onClick={() => setBulkAction(BULK_HIDE)}>
                  Hide Selected in App
                </Button>
                <Button size="small" type="text" icon={<CloseOutlined style={{ color: '#fff' }} />} onClick={() => setSelectedKeys([])} aria-label="Clear selection" />
              </div>
            }

            <Table
                columns={columns}
                rowSelection={isSupportAgent2() ? {
                    selectedRowKeys: selectedKeys,
                    onChange: setSelectedKeys,
                    preserveSelectedRowKeys: true,
                    getCheckboxProps: (record) => ({ disabled: !canUnlink(record) && !canHide(record) })
                } : undefined}
                expandable={{
                    expandedRowRender,
                    expandedRowKeys,
                    onExpandedRowsChange: setExpandedRowKeys,
                }}
                dataSource={visibleAccounts}
                pagination={false}
                loading={loading}
                locale={{ emptyText: 'No accounts match this filter' }}
                rowClassName={(record) => [
                  record.isActive ? "active-row" : "",
                  selectedKeys.includes(record.key) ? "plaid-selected-row" : ""
                ].join(" ")}
            />
          </>
        </>
      )}

      <Modal
          title={"Confirmation"}
          centered
          mask={true}
          maskClosable={true}
          destroyOnClose={true}
          closable={true}
          open={changeActivePlaidModalVisible}
          onOk={changeActivePlaid}
          onCancel={closeChangeActivePlaidModal}
          className="confirm-modal"
      >
        <p>Are you sure you want to set this plaid account as active?</p>
      </Modal>

      <Modal
          title={"Confirmation"}
          centered
          mask={true}
          maskClosable={true}
          destroyOnClose={true}
          closable={true}
          open={changeDeletePlaidModalVisible}
          onOk={deletePlaidAccount}
          onCancel={closeChangeDeletePlaidModal}
          className="confirm-modal"
      >
        <p>Are you sure you want to unlink this plaid account?</p>
      </Modal>

      <Modal
          title={"Confirmation"}
          centered
          mask={true}
          maskClosable={true}
          destroyOnClose={true}
          closable={true}
          open={hidePlaidModalVisible}
          onOk={hidePlaidAccount}
          onCancel={closeHidePlaidModal}
          className="confirm-modal"
      >
        <p>Are you sure you want to hide this plaid account from the user?</p>
        {!selectedPlaidIsDeleted && (
          <p><strong>Warning:</strong> This will also unlink the account if it is not already unlinked.</p>
        )}
      </Modal>

      {/* Bulk confirmation, listing what happens to each selected account */}
      <Modal
          title={bulkAction === BULK_UNLINK
            ? `Unlink ${bulkToRun.length} ${bulkToRun.length === 1 ? 'account' : 'accounts'}?`
            : `Hide ${bulkToRun.length} ${bulkToRun.length === 1 ? 'account' : 'accounts'} in the app?`}
          centered
          open={!!bulkAction}
          onOk={runBulkAction}
          onCancel={() => !bulkRunning && setBulkAction(null)}
          okText={bulkAction === BULK_UNLINK ? `Unlink ${bulkToRun.length}` : `Hide ${bulkToRun.length}`}
          okButtonProps={{ disabled: bulkToRun.length === 0, danger: bulkAction === BULK_UNLINK }}
          confirmLoading={bulkRunning}
          cancelButtonProps={{ disabled: bulkRunning }}
          closable={!bulkRunning}
          maskClosable={!bulkRunning}
      >
        <Text type="secondary">Here's what happens to each one:</Text>
        <div className="plaid-bulk-plan">
          {bulkPlan.map(({ account, step }) => (
            <div key={account.key} className="plaid-bulk-plan-row">
              <span>
                {accountLabel(account)}
                {account.isActive && <Tag color="purple" style={{ marginLeft: 6 }}>Primary</Tag>}
              </span>
              {step
                ? <Tag color={step === "Hide" ? "default" : "warning"}>{step}</Tag>
                : <Tag>Skipped, already {bulkAction === BULK_UNLINK ? "unlinked" : "hidden"}</Tag>}
            </div>
          ))}
        </div>
        {bulkToRun.some(({ step }) => step !== "Hide") &&
          <Alert
            type="warning"
            showIcon
            message={`${bulkToRun.filter(({ step }) => step !== "Hide").length} connected ${bulkToRun.filter(({ step }) => step !== "Hide").length === 1 ? 'account' : 'accounts'} will be unlinked. The user will need to relink to use ${bulkToRun.filter(({ step }) => step !== "Hide").length === 1 ? 'it' : 'them'} again.`} />
        }
        {bulkToRun.some(({ account }) => account.isActive) &&
          <Alert
            style={{ marginTop: 8 }}
            type="error"
            showIcon
            message="This includes the user's primary account. They'll need a new primary account afterwards." />
        }
      </Modal>
      <br/>
      <PlaidEvents />
      <PlaidBalance plaidBalances={plaidBalances} />

    </>
  );
}
